import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question, ModuleId, QuestionType } from '../../types';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { LatexRenderer } from '../ui/LatexRenderer';
import { QuestionRendererFactory } from './renderers/QuestionRendererFactory';
import { FeedbackDisplay } from './feedback/FeedbackDisplay';
import { ArrowRight, Home, BookOpen, Target, Zap, Bug } from 'lucide-react';
import { CheatSheetDrawer } from './CheatSheetDrawer';
import { ConfettiCelebration } from '../ConfettiCelebration';
import { ErrorLogPanel } from '../ErrorLogPanel';
import { getMotivationalMessage, getStreakMessage } from '../../utils/motivationalMessages';
import { selectSmartQuestion } from '../../utils/knowledgeStateEngine';
import { useAppStore } from '../../hooks/useAppStore';
import { getQuestionsByModule, MODULES } from '../../data/questionLoader';
interface PracticeSessionProps {
  moduleId: ModuleId | null;
  questions: Question[];
  onComplete: () => void;
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, userChoice: number) => void;
  cheatSheetViewed: boolean;
  onCheatSheetView: () => void;
  isCrossModule?: boolean;
}
type Phase = 'question' | 'feedback';
// Helper to identify interactive question types
function isInteractiveQuestion(questionType?: QuestionType): boolean {
  if (!questionType) return false;
  return ['cijferend_optellen', 'cijferend_aftrekken', 'geld_samenstellen', 'geld_hergroeperen', 'tijd_analoog', 'tijd_digitaal', 'klok_aflezen'].includes(questionType);
}
// Add validation helper for money questions
function validateMoneyAnswer(userAnswer: string, targetAmount: number): {
  isCorrect: boolean;
  totalAmount: number;
  distractorIndex: number;
} {
  const selections = userAnswer.split(',').map(Number).filter(n => !isNaN(n));
  const totalAmount = selections.reduce((sum, coin) => sum + coin, 0);
  const isCorrect = totalAmount === targetAmount;
  let distractorIndex = 0;
  if (!isCorrect) {
    const difference = Math.abs(totalAmount - targetAmount);
    if (totalAmount < targetAmount) {
      if (difference === 50) distractorIndex = 3; // Forgot 50c
      else if (difference === 100) distractorIndex = 4; // Forgot €1
      else distractorIndex = 1; // Too little
    } else {
      distractorIndex = 2; // Too much
    }
  }
  return {
    isCorrect,
    totalAmount,
    distractorIndex
  };
}
// Helper to format money for display
function formatMoneyDisplay(cents: number): string {
  const euros = Math.floor(cents / 100);
  const remainingCents = cents % 100;
  if (remainingCents === 0) return `€${euros},-`;
  return `€${euros},${remainingCents.toString().padStart(2, '0')}`;
}
export function PracticeSession({
  moduleId,
  questions,
  onComplete,
  onExit,
  onAnswer,
  cheatSheetViewed,
  onCheatSheetView,
  isCrossModule = false
}: PracticeSessionProps) {
  const {
    state
  } = useAppStore();
  const [sessionQuestions, setSessionQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('question');
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [actualIsCorrect, setActualIsCorrect] = useState<boolean | null>(null);
  const [userTypedAnswer, setUserTypedAnswer] = useState<string>('');
  const [userAnswerIndex, setUserAnswerIndex] = useState<number | null>(null);
  const [interactiveReady, setInteractiveReady] = useState(false);
  const [shouldValidateInteractive, setShouldValidateInteractive] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState<Array<{
    text: string;
    originalIndex: number;
  }>>([]);
  const [isCheatSheetOpen, setIsCheatSheetOpen] = useState(false);
  const [isErrorLogOpen, setIsErrorLogOpen] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [shakeError, setShakeError] = useState(false);
  const [motivationalText, setMotivationalText] = useState('');
  const [xpGained, setXpGained] = useState(0);
  const [showStreakCelebration, setShowStreakCelebration] = useState(false);
  const [streakMessage, setStreakMessage] = useState('');
  const totalModuleQuestions = moduleId ? getQuestionsByModule(moduleId).length : 0;
  const isFocusMode = isCrossModule || questions.length < totalModuleQuestions && questions.length > 0;
  const moduleTitle = moduleId ? MODULES.find(m => m.id === moduleId)?.title || 'Oefenen' : isCrossModule ? 'Superkracht Training' : 'Oefenen';
  // Accessibility: Escape key to exit
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isCheatSheetOpen) {
        onExit();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onExit, isCheatSheetOpen]);
  useEffect(() => {
    if (questions.length > 0 && sessionQuestions.length === 0) {
      if (isCrossModule) {
        setSessionQuestions(questions);
      } else if (moduleId) {
        const recentQuestions = state.progress[moduleId]?.recentQuestions || [];
        const smartQuestions: Question[] = [];
        for (let i = 0; i < Math.min(10, questions.length); i++) {
          const nextQuestionId = selectSmartQuestion(moduleId, state.questionStates, [...recentQuestions, ...smartQuestions.map(q => q.id)]);
          if (nextQuestionId) {
            const question = getQuestionsByModule(moduleId).find(q => q.id === nextQuestionId);
            if (question) {
              smartQuestions.push(question);
            }
          }
        }
        if (smartQuestions.length === 0) {
          const shuffled = [...questions].sort(() => Math.random() - 0.5);
          setSessionQuestions(shuffled.slice(0, 10));
        } else {
          setSessionQuestions(smartQuestions);
        }
      }
    }
  }, [questions, sessionQuestions.length, state.questionStates, state.progress, moduleId, isCrossModule]);
  const currentQuestion = sessionQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === sessionQuestions.length - 1;
  // Shuffle options when question changes
  useEffect(() => {
    if (currentQuestion) {
      setShuffledOptions(shuffleOptionsWithIndices(currentQuestion.options));
      setSelectedOption(null);
      setUserAnswerIndex(null);
      setUserTypedAnswer('');
      setInteractiveReady(false);
      setShouldValidateInteractive(false);
    }
  }, [currentQuestion]);
  const handleCheatSheetClose = () => {
    setIsCheatSheetOpen(false);
    if (!cheatSheetViewed) {
      onCheatSheetView();
    }
  };
  // Handle completion from interactive components with distractor matching
  const handleInteractiveComplete = (userAnswer: string) => {
    setUserTypedAnswer(userAnswer);
    let isCorrect: boolean;
    let distractorIndex: number;
    let displayAnswer: string = userAnswer;
    // Check question type for validation
    if (currentQuestion.questionType === 'geld_samenstellen') {
      // Money validation
      const validation = validateMoneyAnswer(userAnswer, currentQuestion.correctAnswer);
      isCorrect = validation.isCorrect;
      distractorIndex = validation.distractorIndex;
      displayAnswer = formatMoneyDisplay(validation.totalAmount);
      // Update typed answer for display
      setUserTypedAnswer(displayAnswer);
    } else if (currentQuestion.questionType === 'cijferend_optellen' || currentQuestion.questionType === 'cijferend_aftrekken') {
      // Column arithmetic validation (existing)
      const normalizedUserAnswer = userAnswer.replace(/\./g, '');
      const correctAnswer = currentQuestion.options[currentQuestion.correctAnswer];
      const normalizedCorrectAnswer = correctAnswer.replace(/\./g, '');
      isCorrect = normalizedUserAnswer === normalizedCorrectAnswer;
      distractorIndex = currentQuestion.options.findIndex(opt => opt.replace(/\./g, '') === normalizedUserAnswer);
      if (distractorIndex === -1) distractorIndex = 0;
    } else if (currentQuestion.questionType === 'klok_aflezen') {
      // Clock validation
      const correctAnswer = currentQuestion.options[currentQuestion.correctAnswer];
      // Normalize answers (ensure HH:MM:SS format matches)
      // User answer comes as HH:MM:SS from renderer
      // Correct answer is stored in options
      // Simple string comparison for now, assuming renderer ensures format
      isCorrect = userAnswer === correctAnswer;
      // Find distractor index
      distractorIndex = currentQuestion.options.findIndex(opt => opt === userAnswer);
      // If exact match not found in options (e.g. user entered random wrong time),
      // default to generic wrong answer (usually index 1 or similar)
      if (!isCorrect && distractorIndex === -1) {
        // Try to be smart about distractors
        // If minutes are correct but hour is wrong -> potentially index 1
        // If hour is correct but minutes wrong -> potentially index 2
        // For now, default to 1
        distractorIndex = 1;
      }
      // Format for display
      displayAnswer = userAnswer;
    } else {
      // Fallback
      isCorrect = false;
      distractorIndex = 0;
    }
    setActualIsCorrect(isCorrect);
    let xp = 0;
    if (isCorrect) {
      if (currentQuestion.difficulty >= 4) {
        xp = 30; // E6 level
      } else if (currentQuestion.difficulty === 3) {
        xp = 25; // M6+ level
      } else {
        xp = 20; // M6 level
      }
    } else {
      xp = 5;
    }
    setXpGained(xp);
    if (isCorrect) {
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      setCorrectCount(prev => prev + 1);
      const message = getStreakMessage(newStreak);
      if (message) {
        setStreakMessage(message);
        setShowStreakCelebration(true);
      }
    } else {
      setCurrentStreak(0);
      setIncorrectCount(prev => prev + 1);
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
    }
    const message = getMotivationalMessage({
      isCorrect,
      streak: currentStreak,
      questionNumber: currentQuestionIndex + 1,
      totalQuestions: sessionQuestions.length
    });
    setMotivationalText(message);
    // Store the index for FeedbackDisplay
    setUserAnswerIndex(distractorIndex);
    onAnswer(currentQuestion.id, isCorrect, distractorIndex);
    setSelectedOption(null);
    setPhase('feedback');
  };
  // Unified handler for all question types via factory
  const handleRendererAnswer = (userAnswer: string | number) => {
    const isInteractive = isInteractiveQuestion(currentQuestion.questionType);
    if (isInteractive) {
      // For interactive questions, immediately process the answer
      handleInteractiveComplete(userAnswer as string);
    } else {
      // For multiple choice, just store the selection
      setUserAnswerIndex(userAnswer as number);
      setSelectedOption(userAnswer as number);
    }
  };
  // Submit answer for ALL question types
  const submitAnswer = () => {
    const isInteractive = isInteractiveQuestion(currentQuestion.questionType);
    if (isInteractive) {
      setShouldValidateInteractive(true);
      return;
    }
    if (userAnswerIndex === null) return;
    const originalIndex = userAnswerIndex;
    const isCorrect = originalIndex === currentQuestion.correctAnswer;
    let xp = 0;
    if (isCorrect) {
      if (currentQuestion.difficulty >= 4) {
        xp = 30; // E6 level
      } else if (currentQuestion.difficulty === 3) {
        xp = 25; // M6+ level
      } else {
        xp = 20; // M6 level
      }
    } else {
      xp = 5;
    }
    setXpGained(xp);
    if (isCorrect) {
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      setCorrectCount(prev => prev + 1);
      const message = getStreakMessage(newStreak);
      if (message) {
        setStreakMessage(message);
        setShowStreakCelebration(true);
      }
    } else {
      setCurrentStreak(0);
      setIncorrectCount(prev => prev + 1);
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
    }
    const message = getMotivationalMessage({
      isCorrect,
      streak: currentStreak,
      questionNumber: currentQuestionIndex + 1,
      totalQuestions: sessionQuestions.length
    });
    setMotivationalText(message);
    onAnswer(currentQuestion.id, isCorrect, originalIndex);
    setPhase('feedback');
  };
  const nextQuestion = () => {
    if (isLastQuestion) {
      onComplete();
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedOption(null);
      setUserAnswerIndex(null);
      setUserTypedAnswer('');
      setActualIsCorrect(null);
      setInteractiveReady(false);
      setShouldValidateInteractive(false);
      setPhase('question');
    }
  };
  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (phase !== 'question' || isCheatSheetOpen) return;
      if (e.key === 'Enter') {
        const isInteractive = isInteractiveQuestion(currentQuestion?.questionType);
        if (isInteractive && interactiveReady) {
          submitAnswer();
        } else if (!isInteractive && userAnswerIndex !== null) {
          submitAnswer();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, isCheatSheetOpen, userAnswerIndex, interactiveReady, currentQuestion]);
  // Get difficulty badge styling
  const getDifficultyBadge = (difficulty?: 1 | 2 | 3 | 4 | 5) => {
    if (!difficulty) return null;
    const badges = {
      1: {
        label: 'M6',
        color: 'bg-emerald-500',
        icon: null
      },
      2: {
        label: 'M6',
        color: 'bg-emerald-600',
        icon: null
      },
      3: {
        label: 'M6+',
        color: 'bg-amber-500',
        icon: null
      },
      4: {
        label: 'E6',
        color: 'bg-rose-500',
        icon: <Zap className="w-3 h-3" data-id="element-1098" />
      },
      5: {
        label: 'E6',
        color: 'bg-rose-600',
        icon: <Zap className="w-3 h-3" data-id="element-1099" />
      }
    };
    const badge = badges[difficulty];
    return <motion.div initial={{
      scale: 0,
      rotate: -180
    }} animate={{
      scale: 1,
      rotate: 0
    }} transition={{
      type: 'spring',
      stiffness: 200,
      damping: 10
    }} className={`absolute top-6 right-6 ${badge.color} text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-md z-10`} data-id="element-1100">
        {badge.icon}
        {badge.label}
      </motion.div>;
  };
  // Empty state
  if (questions.length === 0) {
    return <div className="min-h-screen flex items-center justify-center p-4" data-id="element-1101">
        <div className="text-center max-w-md" data-id="element-1102">
          <div className="text-6xl mb-4" data-id="element-1103">🎉</div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-1104">
            Je hebt alle vragen verslagen!
          </h2>
          <p className="text-slate-600 mb-6" data-id="element-1105">
            Geweldig werk! Er zijn momenteel geen nieuwe vragen beschikbaar voor
            deze module.
          </p>
          <Button onClick={onExit} variant="primary" size="lg" data-id="element-1106">
            Terug naar dashboard
          </Button>
        </div>
      </div>;
  }
  // Loading state
  if (!currentQuestion) {
    return <div className="min-h-screen flex items-center justify-center" role="status" aria-live="polite" data-id="element-1107">
        <div className="text-center" data-id="element-1108">
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear'
        }} className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4" data-id="element-1109" />
          <p className="text-slate-600" data-id="element-1110">Slimme vragen selecteren...</p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 flex flex-col max-w-3xl mx-auto p-4 md:p-6" data-id="element-1111">
      <ConfettiCelebration trigger={showStreakCelebration} message={streakMessage} data-id="element-1112" />

      <CheatSheetDrawer isOpen={isCheatSheetOpen} onClose={handleCheatSheetClose} moduleId={moduleId!} data-id="element-1113" />

      <ErrorLogPanel state={state} isOpen={isErrorLogOpen} onClose={() => setIsErrorLogOpen(false)} data-id="element-1114" />

      {/* Header */}
      <div className="flex justify-between items-center mb-6" data-id="element-1115">
        <motion.button whileHover={{
        scale: 1.1
      }} whileTap={{
        scale: 0.9
      }} onClick={onExit} aria-label="Terug naar dashboard" className="text-slate-400 hover:text-slate-600 transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center" data-id="element-1116">
          <Home className="w-6 h-6" data-id="element-1117" />
        </motion.button>

        <div className="flex items-center gap-2" data-id="element-1118">
          <motion.button whileHover={{
          scale: 1.1
        }} whileTap={{
          scale: 0.9
        }} onClick={() => setIsErrorLogOpen(true)} aria-label="Open error log" className="text-amber-400 hover:text-amber-600 transition-colors min-h-[48px] flex items-center justify-center gap-2 px-3" data-id="element-1119">
            <Bug className="w-5 h-5" data-id="element-1120" />
            {state.errorLog && state.errorLog.length > 0 && <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-1121">
                {state.errorLog.length}
              </span>}
          </motion.button>

          <motion.button whileHover={{
          scale: 1.1
        }} whileTap={{
          scale: 0.9
        }} onClick={() => setIsCheatSheetOpen(true)} aria-label="Open uitleg en spiekbriefje" className="text-indigo-400 hover:text-indigo-600 transition-colors min-h-[48px] flex items-center justify-center gap-2 px-3" data-id="element-1122">
            <BookOpen className="w-5 h-5" data-id="element-1123" />
            <span className="text-sm font-medium hidden sm:inline" data-id="element-1124">Uitleg</span>
          </motion.button>
        </div>

        <div className="flex-1 mx-4 sm:mx-8 h-3 bg-slate-200 rounded-full overflow-hidden shadow-inner" role="progressbar" aria-valuenow={Math.round((currentQuestionIndex + 1) / sessionQuestions.length * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={`Vraag ${currentQuestionIndex + 1} van ${sessionQuestions.length}`} data-id="element-1125">
          <motion.div className="h-full bg-indigo-600 rounded-full" initial={{
          width: 0
        }} animate={{
          width: `${(currentQuestionIndex + 1) / sessionQuestions.length * 100}%`
        }} transition={{
          duration: 0.5,
          ease: 'easeInOut'
        }} data-id="element-1126" />
        </div>

        <div className="flex items-center gap-3" data-id="element-1127">
          {currentStreak > 0 && <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1.5 shadow-sm" data-id="element-1128">
              🔥 {currentStreak}
            </motion.div>}
          <span className="text-sm font-bold text-slate-500 bg-white px-3 py-1 rounded-full shadow-sm border border-slate-100" data-id="element-1129">
            {currentQuestionIndex + 1}/{sessionQuestions.length}
          </span>
        </div>
      </div>

      {/* Focus Mode Indicator */}
      {isFocusMode && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-6 bg-indigo-100 border-2 border-indigo-300 rounded-xl p-3 text-center shadow-sm" data-id="element-1130">
          <p className="text-sm font-bold text-indigo-900 flex items-center justify-center gap-2" data-id="element-1131">
            <Target className="w-4 h-4" data-id="element-1132" />
            Superkracht Training
          </p>
          <p className="text-xs text-indigo-700 mt-1" data-id="element-1133">
            {isCrossModule ? `Doordenker ${currentQuestionIndex + 1} van ${sessionQuestions.length}` : 'Je oefent nu alleen de doordenkers'}
          </p>
        </motion.div>}

      <AnimatePresence mode="wait" data-id="element-1134">
        {/* QUESTION PHASE */}
        {phase === 'question' && <motion.div key={`question-${currentQuestionIndex}`} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.3
      }} className="flex-1 flex flex-col" data-id="element-1135">
            <Card className="p-8 md:p-10 mb-6 flex-1 flex flex-col justify-center relative shadow-xl border-slate-100" data-id="element-1136">
              <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="absolute top-6 left-8 text-sm font-bold text-indigo-500 uppercase tracking-wider bg-indigo-50 px-3 py-1 rounded-full" data-id="element-1137">
                {moduleTitle}
              </motion.div>

              {getDifficultyBadge(currentQuestion.difficulty)}

              <div className="mt-8" data-id="element-1138">
                {currentQuestion.context && <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.15
            }} className="mb-8 p-6 bg-slate-50 rounded-xl border-l-4 border-indigo-400 text-slate-700 italic text-lg shadow-sm" data-id="element-1139">
                    <LatexRenderer content={currentQuestion.context} data-id="element-1140" />
                  </motion.div>}

                <motion.h2 initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-2xl md:text-3xl font-bold text-slate-900 mb-10 leading-snug" data-id="element-1141">
                  <LatexRenderer content={currentQuestion.question} data-id="element-1142" />
                </motion.h2>

                <QuestionRendererFactory question={currentQuestion} onAnswer={handleRendererAnswer} onReadyChange={setInteractiveReady} shouldValidate={shouldValidateInteractive} data-id="element-1143" />
              </div>
            </Card>

            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} data-id="element-1144">
              <Button fullWidth size="lg" disabled={isInteractiveQuestion(currentQuestion.questionType) ? !interactiveReady : userAnswerIndex === null} onClick={submitAnswer} className="h-14 text-lg shadow-lg hover:shadow-xl transition-shadow relative" data-id="element-1145">
                Antwoord Controleren
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-mono bg-white/20 px-2 py-1 rounded hidden md:inline-block" data-id="element-1146">
                  Enter
                </span>
              </Button>
            </motion.div>
          </motion.div>}

        {/* FEEDBACK PHASE */}
        {phase === 'feedback' && <motion.div key="feedback" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="flex-1 flex flex-col" data-id="element-1147">
            {(() => {
          const isInteractive = isInteractiveQuestion(currentQuestion.questionType);
          const wasCorrect = isInteractive ? actualIsCorrect === true : userAnswerIndex !== null && userAnswerIndex === currentQuestion.correctAnswer;
          const userAnswerDisplay = isInteractive ? userTypedAnswer : userAnswerIndex !== null ? currentQuestion.options[userAnswerIndex] : '';
          // CRITICAL FIX: Always pass userAnswerIndex to FeedbackDisplay
          const finalUserAnswerIndex = isInteractive ? userAnswerIndex // Already set in handleInteractiveComplete
          : userAnswerIndex; // Already set for multiple choice
          return <FeedbackDisplay question={currentQuestion} wasCorrect={wasCorrect} userAnswer={userAnswerDisplay} userAnswerIndex={finalUserAnswerIndex ?? undefined} motivationalText={motivationalText} xpGained={xpGained} onNext={nextQuestion} isLastQuestion={isLastQuestion} mode="full" data-id="element-1148" />;
        })()}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// Helper function to shuffle options while tracking original indices
function shuffleOptionsWithIndices(options: string[]): Array<{
  text: string;
  originalIndex: number;
}> {
  return options.map((text, originalIndex) => ({
    text,
    originalIndex
  })).sort(() => Math.random() - 0.5);
}